import React, { useEffect, useState } from 'react';

import { useTranslation } from '../../../translations';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { ReactPdf } from '../../../common/components/PdfViewer/ReactPdf';
import {
  CreateUpdateContractDtoStatusEnum,
  ResponseError,
  SignaturePositionSchemaDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { SignatureFrame } from '../../../common/pokCore/validation/signatureFrame';

import { SignatureFrameMarker } from './SignatureFrameMarker';
import {
  SignaturePositionRow,
  SignaturePositionsPanel,
} from './SignaturePositionsPanel';
import {
  getDefaultSignaturePosition,
  PdfPageSize,
} from './signatureFrameGeometry';
import { useSignaturePreviewImages } from './useSignaturePreviewImages';
import '../../../theme/contractSignaturePdf.scss';

interface ContractSignaturePdfProps {
  file: Blob;
  isPdfEncrypted: boolean;
  signaturePositions: SignaturePositionSchemaDto[];
  saveSignaturePositions: (
    positions: SignaturePositionSchemaDto[],
  ) => Promise<void>;
  editable: boolean;
  showPositions: boolean;
  status?: CreateUpdateContractDtoStatusEnum;
}

// The one position being placed or moved; `index` is its number - 1.
interface DraftSignPosition {
  index: number;
  isNew: boolean;
  position: SignaturePositionSchemaDto;
}

export const ContractSignaturePdf: React.FC<
  ContractSignaturePdfProps
> = props => {
  const { t, tk } = useTranslation('commonLng');
  const notifications = useNotifications();
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [pageSize, setPageSize] = useState<PdfPageSize>();
  const [draft, setDraft] = useState<DraftSignPosition>();
  const [saving, setSaving] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>();

  const positions = props.signaturePositions;
  const previewImages = useSignaturePreviewImages(props.showPositions);

  useEffect(() => {
    if (!draft) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) {
        setDraft(undefined);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [draft, saving]);

  const rows: SignaturePositionRow[] = positions.map((position, index) =>
    draft && !draft.isNew && draft.index === index
      ? { position: draft.position, draft: true }
      : { position, draft: false },
  );
  if (draft?.isNew) {
    rows.push({ position: draft.position, draft: true });
  }

  const addDisabledReason = props.isPdfEncrypted
    ? t(tk.attachment.encryptedPDF)
    : positions.length >= SignatureFrame.maxPositions
      ? t(tk.pdfViewer.maxSignatures, { max: SignatureFrame.maxPositions })
      : undefined;

  const save = async (next: SignaturePositionSchemaDto[]) => {
    setSaving(true);
    try {
      await props.saveSignaturePositions(next);
      setDraft(undefined);
    } catch (error) {
      notifications.caughtError(error as ResponseError);
    } finally {
      setSaving(false);
    }
  };

  const add = () => {
    if (!pageSize) {
      return;
    }
    const anchor = getDefaultSignaturePosition(pageSize);
    setDraft({
      index: positions.length,
      isNew: true,
      position: { ...anchor, page: pageNumber },
    });
  };

  const move = (index: number) => {
    setPageNumber(positions[index].page);
    setDraft({ index, isNew: false, position: positions[index] });
  };

  const confirm = () => {
    if (!draft) {
      return;
    }
    const next = draft.isNew
      ? [...positions, draft.position]
      : positions.map((position, index) =>
          index === draft.index ? draft.position : position,
        );
    void save(next);
  };

  const remove = (index: number) => {
    void save(positions.filter((_, i) => i !== index));
  };

  const changePage = (page: number) => {
    setPageNumber(page);
    setDraft(prev => prev && { ...prev, position: { ...prev.position, page } });
  };

  const renderOverlay = (page: PdfPageSize & { pageNumber: number }) =>
    rows.map(
      (row, index) =>
        row.position.page === page.pageNumber && (
          <SignatureFrameMarker
            key={index}
            number={index + 1}
            position={row.position}
            pageSize={page}
            draft={row.draft}
            highlighted={highlightedIndex === index}
            saving={saving}
            onMove={(x, y) =>
              setDraft(
                prev =>
                  prev && { ...prev, position: { ...prev.position, x, y } },
              )
            }
            onConfirm={confirm}
            onCancel={() => setDraft(undefined)}
            onHoverChange={hovered =>
              setHighlightedIndex(hovered ? index : undefined)
            }
            onSelect={
              props.editable && !draft && !saving
                ? () => move(index)
                : undefined
            }
            signatureImage={previewImages.signature}
            stampImage={previewImages.stamp}
          />
        ),
    );

  return (
    <ReactPdf
      file={props.file}
      pageNumber={pageNumber}
      onPageNumberChange={changePage}
      onNumPagesChange={setNumPages}
      header={
        props.showPositions && (
          <SignaturePositionsPanel
            rows={rows}
            numPages={numPages}
            editable={props.editable}
            editing={!!draft}
            saving={saving}
            addDisabledReason={addDisabledReason}
            deleteLastBlocked={
              props.status ===
                CreateUpdateContractDtoStatusEnum.WaitingForSigning &&
              positions.length <= 1
            }
            highlightedIndex={highlightedIndex}
            onHighlight={setHighlightedIndex}
            onAdd={add}
            onMove={move}
            onDelete={remove}
            onGoToPage={setPageNumber}
            onConfirm={confirm}
            onCancel={() => setDraft(undefined)}
          />
        )
      }
      onPageInfoChange={setPageSize}
      renderPageOverlay={page => props.showPositions && renderOverlay(page)}
    />
  );
};
